import * as ScrollArea from "@radix-ui/react-scroll-area";
import * as SelectPrimitive from "@radix-ui/react-select";
import { debounce } from "lodash";
import { matchSorter } from "match-sorter";
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import useOnClickOutside from "src/hooks/useOnClickOutside";
import { cn } from "src/lib/cn";
import { SudoRadioButtons } from "../SudoRadioButtons";
import SelectBody from "./SelectBody";
import { selectVariants } from "./shared";

/**
 * @typedef {'primary' | 'secondary' | 'tertiary' | 'secondaryGrey' | 'tertiaryGrey' | 'translucent'} SelectVariant
 * @typedef {'button' | 'radiobutton'} MenuType
 * @typedef {'default' | 'success' | 'danger' | 'warning'} IconStatus
 * @typedef {{ value: string, label: string, subLabel?: string, inlineSubLabel?: string, subMenu?: React.ReactNode, icon?: { label: string, src: string, status: IconStatus } }} Option
 * @typedef {'left' | 'center'} OptionAlignment
 * @typedef {'default' | 'small'} SelectSize
 */

const MenuSelect = forwardRef(
  /**
   * @param {Object} props
   * @param {Array<Option>} props.options
   * @param {string} props.value
   * @param {string} props.label
   * @param {string} [props.placeholder="Select an option"]
   * @param {MenuType} [props.menuType="button"]
   * @param {string} [props.menuLabel=""]
   * @param {boolean} [props.disabled=false]
   * @param {SelectVariant} [props.variant="tertiary"]
   * @param {string} [props.className]
   * @param {JSX.Element | string} [props.prefix]
   * @param {JSX.Element | string} [props.suffix]
   * @param {string} [props.maxHeight="300px"]
   * @param {boolean} [props.isOpen=false]
   * @param {boolean} [props.canAddCustom=false]
   * @param {string} [props.searchLabel=""]
   * @param {(value: string) => void} [props.onChange]
   * @param {(event: React.FocusEvent<HTMLButtonElement>) => void} [props.onOpenChange]
   * @param {boolean} [props.showSublabelInTrigger=false]
   * @param {React.ReactNode} [props.children]
   * @param {OptionAlignment} [props.optionAlignment="center"]
   * @param {string} [props.collisionBoundarySelector=null]
   * @param {string} [props.portalContainerSelector=null]
   * @param {SelectSize} [props.size="default"]
   * @param {string} [props.menuSide="bottom"]
   * @param {string} [props.menuAlign="center"]
   * @param {number} [props.sideOffset=0]
   * @param {number} [props.alignOffset=0]
   * @param {React.Ref<HTMLButtonElement>} ref
   * @returns {JSX.Element}
   */
  (
    {
      className,
      label,
      placeholder,
      menuType = "button",
      menuLabel = "",
      variant = "tertiary",
      disabled = false,
      prefix,
      suffix,
      options,
      value,
      canAddCustom = false,
      searchLabel = "",
      onChange,
      onOpenChange,
      maxHeight = "300px",
      isOpen,
      showSublabelInTrigger = false,
      children,
      optionAlignment = "left",
      collisionBoundarySelector = null,
      portalContainerSelector = null,
      size = "default",
      menuSide = "bottom",
      menuAlign = "center",
      sideOffset = 0,
      alignOffset = 0,
    },
    ref
  ) => {
    const inputRef = useRef(null);
    const triggerRefLocal = useRef(null);
    const collisionBoundary = useRef(null);
    const portalContainer = useRef(null);
    const instanceIdRef = useRef(Math.random().toString(36).slice(2));
    const [open, setOpen] = useState(isOpen);
    const [optionsState, setOptionsState] = useState(options);
    const [selectedValue, setSelectedValue] = useState(value);
    const [selectedOption, setSelectedOption] = useState(options.find((option) => option.value === value));
    const [customValue, setCustomValue] = useState("");
    const [dynamicMaxHeight, setDynamicMaxHeight] = useState(maxHeight);
    useOnClickOutside(ref, () => {
      setOpen(false);
    });

    useEffect(() => {
      setOptionsState(options);
    }, [options]);

    const handleValueChange = (value) => {
      setSelectedValue(value);
      setOpen(false);

      if (canAddCustom && value === customValue) {
        setOptionsState((prev) => [...prev, { value: customValue, label: customValue }]);
      }
      setCustomValue("");

      if (onChange) {
        onChange(value);
      }
    };

    useEffect(() => {
      if (value !== selectedValue) {
        setSelectedValue(value);
      }
    }, [value, selectedValue]);

    useEffect(() => {
      const option = optionsState.find((option) => option.value === selectedValue);
      setSelectedOption(option);
    }, [selectedValue, optionsState]);

    const handleOpenChange = (open) => {
      setOpen(open);

      if (open) {
        // Announce that this select has opened so others can close
        try {
          window?.dispatchEvent(new CustomEvent("sudokit:select-open", { detail: { id: instanceIdRef.current } }));
        } catch (_) {}
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 0);
      }

      if (onOpenChange) {
        onOpenChange(open);
      }
    };

    const debounceSetValue = debounce((value) => {
      setCustomValue(value);
    }, 150);

    const matches = useMemo(() => {
      if (!customValue) return optionsState || [];
      const keys = ["label", "value", "subLabel"];
      const matches = matchSorter(optionsState, customValue, { keys }) || [];

      // Add the "Create foo" option if no matches are found
      if (matches.length === 0) {
        matches.push({
          value: customValue,
          label: `Create ${customValue}`,
        });
      }

      return matches;
    }, [customValue, optionsState]);

    useEffect(() => {
      if (!collisionBoundarySelector) {
        return;
      }

      collisionBoundary.current = document?.querySelector(collisionBoundarySelector);
    }, [collisionBoundarySelector]);

    useEffect(() => {
      if (!portalContainerSelector) {
        return;
      }
      portalContainer.current = document?.querySelector(portalContainerSelector);
    }, [portalContainerSelector]);

    // Update dynamic maxHeight when menu opens
    // so that if the full height of the menu is not visible, the menu will scroll
    useEffect(() => {
      if (open && ref && ref.current) {
        const triggerRect = ref.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - triggerRect.bottom - 16;
        const spaceAbove = triggerRect.top - 16;
        const minHeight = size === "small" ? 150 : 200;
        const availableSpace = Math.max(spaceBelow, spaceAbove, minHeight);
        setDynamicMaxHeight(`${availableSpace}px`);
      }
    }, [open, ref, size]);

    const subLabel = selectedOption && selectedOption.subLabel ? selectedOption.subLabel : "";
    const showSubLabel = subLabel && showSublabelInTrigger;

    // Size-based styling
    const sizeClasses = size === "small" ? "h-[28px] px-2 py-1 text-xs" : "h-[40px] px-3 py-2";

    const iconSizeClasses = size === "small" ? "h-4 w-4 mr-1.5" : "h-6 w-6 mr-2";

    const dropdownIconSizeClasses = size === "small" ? "ml-2 h-3 w-3 flex-shrink-0 self-center" : "ml-4 mt-1 h-4 w-4 flex-shrink-0";

    const textSizeClasses = size === "small" ? "text-xs" : "text-base";

    const roundingClasses = size === "small" ? "rounded-[7px]" : "rounded-[10px]";

    const menuWidthClasses = size === "small" ? "w-[280px]" : "w-[457px]";

    // Always declare hooks at top level
    // Close when another sudokit select opens
    useEffect(() => {
      const handler = (e) => {
        if (e && e.detail && e.detail.id !== instanceIdRef.current) {
          setOpen(false);
        }
      };
      window?.addEventListener("sudokit:select-open", handler);
      return () => window?.removeEventListener("sudokit:select-open", handler);
    }, []);

    if (menuType === "radiobutton") {
      return (
        <SelectPrimitive.Root
          value={selectedValue}
          onValueChange={handleValueChange}
          open={open}
          onOpenChange={handleOpenChange}
        >
          <SelectPrimitive.Trigger
            ref={ref}
            className={`sudokit ${cn(selectVariants({ variant }))} group flex items-center transition-all justify-between border-solid ${roundingClasses} ${
              variant !== "translucent" ? "bg-transparent" : ""
            } ${sizeClasses} shadow-[0_1px_2px_0_rgba(0,0,0,0.08)] [&_path]:fill-current`}
            aria-label={label}
          >
            <div className="flex items-center">
              {selectedOption && selectedOption.icon && (
                <SVG
                  src={selectedOption.icon.src}
                  className={`${iconSizeClasses} ${selectVariants({ variant: variant === "translucent" ? "icontranslucent" : "iconprimary" })}`}
                />
              )}
              {prefix}
              <SelectPrimitive.Value placeholder={placeholder}>
                {selectedOption ? (
                  <div>
                    <p className={`${subLabel ? `font-inter font-medium ${showSubLabel ? (size === "small" ? "text-xs" : "text-sm") : textSizeClasses} mt-[-1px]` : textSizeClasses}`}>
                      {selectedOption.label}
                    </p>
                    {showSubLabel && <p className={`font-inter ${size === "small" ? "text-[8px]" : "text-[10px]"} leading-[130%] font-semibold tracking-[2%] mt-[-1px]`}>{subLabel}</p>}
                  </div>
                ) : (
                  placeholder
                )}
              </SelectPrimitive.Value>
              {suffix}
            </div>
            <SelectPrimitive.Icon className={size === "small" ? "flex items-center" : ""}>
              <SVG
                src="/assets/settings_dropdown_icon.svg"
                className={`${dropdownIconSizeClasses} ${cn(selectVariants({ variant: `icon${variant}` }))}`}
              />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>
          <SelectPrimitive.Portal container={portalContainer.current}>
            <SelectPrimitive.Content
              avoidCollisions={false}
              position="popper"
              side={menuSide}
              align={menuAlign}
              collisionBoundary={collisionBoundary.current}
              collisionPadding={{ top: 100, bottom: 100 }}
              sideOffset={sideOffset}
              alignOffset={alignOffset}
              className={`${
                menuSide === "top" ? "origin-bottom" : "origin-top"
              } [&[data-state='open']]:animate-flyout ${menuWidthClasses} bg-app-menu-color shadow-card border border-app-menu-color-hover my-2 p-1 rounded-xl overflow-hidden z-[10000] ${className}`}
              style={{
                maxHeight: dynamicMaxHeight,
              }}
            >
              {menuLabel && <p className="font-inter font-medium text-app-grey-0 text-base px-2 pb-2 pt-1 text-start">{menuLabel}</p>}
              <ScrollArea.Root
                className={`w-full box-border px-2 pb-2`}
                type={"auto"}
                style={{
                  maxHeight: dynamicMaxHeight,
                  overflowY: "auto",
                  height: undefined,
                }}
              >
                <SudoRadioButtons
                  value={selectedValue}
                  options={matches.map((match) => ({
                    value: match.value,
                    label: match.label,
                    description: match.subLabel,
                  }))}
                  size="lg"
                  orientation="vertical"
                  onChange={handleValueChange}
                />
                {children}
                <ScrollArea.Scrollbar
                  className="w-2 bg-gray-200 transition-colors duration-200 rounded-xl h-[97%]"
                  orientation="vertical"
                >
                  <ScrollArea.Thumb className="bg-gray-300 rounded-xl" />
                </ScrollArea.Scrollbar>
              </ScrollArea.Root>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>
      );
    }

    return (
      <>
        <SelectPrimitive.Root
          value={selectedOption ? selectedOption.value : undefined}
          onValueChange={handleValueChange}
          disabled={disabled}
          open={open}
          onOpenChange={handleOpenChange}
        >
          <SelectPrimitive.Trigger
            ref={triggerRefLocal}
            className={`sudokit ${cn(selectVariants({ variant }))} group flex items-center justify-between border-solid ${roundingClasses} ${
              variant !== "translucent" ? "bg-transparent" : ""
            } ${sizeClasses} shadow-[0_1px_2px_0_rgba(0,0,0,0.08)] [&_path]:fill-current`}
            aria-label={label}
          >
            <div className="flex items-center">
              {selectedOption && selectedOption.icon && (
                <SVG
                  src={selectedOption.icon.src}
                  className={`${iconSizeClasses} ${selectVariants({ variant: variant === "translucent" ? "icontranslucent" : "iconprimary" })}`}
                />
              )}
              {prefix}
              <SelectPrimitive.Value placeholder={placeholder}>
                {selectedOption ? (
                  <div>
                    <p className={`${subLabel ? `font-inter font-medium ${showSubLabel ? (size === "small" ? "text-xs" : "text-sm") : textSizeClasses} mt-[-1px]` : textSizeClasses}`}>
                      {selectedOption.label}
                    </p>
                    {showSubLabel && <p className={`font-inter ${size === "small" ? "text-[8px]" : "text-[10px]"} leading-[130%] font-semibold tracking-[2%] mt-[-1px]`}>{subLabel}</p>}
                  </div>
                ) : (
                  placeholder
                )}
              </SelectPrimitive.Value>
              {suffix}
            </div>
            <SelectPrimitive.Icon className={size === "small" ? "flex items-center" : ""}>
              <SVG
                src="/assets/settings_dropdown_icon.svg"
                className={`${dropdownIconSizeClasses} ${cn(selectVariants({ variant: `icon${variant}` }))}`}
              />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>

          {menuType === "button" && (
            <SelectBody
              maxHeight={maxHeight}
              selectedValue={selectedValue}
              customValue={customValue}
              searchLabel={searchLabel}
              canAddCustom={canAddCustom}
              matches={matches}
              debounceSetValue={debounceSetValue}
              inputRef={inputRef}
              menuLabel={menuLabel}
              optionAlignment={optionAlignment}
              collisionBoundary={collisionBoundary}
              open={open}
              className={className}
              portalContainer={portalContainer}
              triggerRef={triggerRefLocal}
              onOptionClick={handleValueChange}
              onOpenChange={handleOpenChange}
              selectSize={size}
              // Plumb positioning controls through
              side={menuSide}
              align={menuAlign}
              sideOffset={sideOffset}
              alignOffset={alignOffset}
            >
              {children}
            </SelectBody>
          )}
        </SelectPrimitive.Root>
      </>
    );
  }
);

MenuSelect.displayName = "MenuSelect";

export default MenuSelect;
